import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import imageLoadError from 'utils/image-load-error';

import colors from 'styles/colors';
import appHelpers from 'utils/app-urls';
import cornerLockIcon from './lock-icon.svg';

import { getDisplayAssetCode } from '@altify/utils';
import { GradientOrColorView } from 'components/GradientOrColorView/GradientOrColorView';
import { useProduct } from 'hooks/products/useProducts';
import theme from '../../styles/colors';

const cdnURL = appHelpers.getCDNUrl();

interface CurrencyIconProps {
  /**
   * - fetches the corresponding Crypto/Theme/Currency/Bundle icon from the CDN
   * - one of **('BTC', 'ETH', 'ZAR', 'GBP', 'RVX10', 'CBDX', etc.)**
   * - country codes also work (e.g. 'US', 'GB', 'ZA', 'EU', 'CH')
   */
  currencyCode?: string | null;
  //adding this so it matches the props with the app
  code?: string | null;
  /** className prop to override the container <div />. The child <img /> fills the parent with 'w-full h-auto'  */
  className?: string;
  cornerIcon?: boolean;
  constituent?: boolean;
  inactive?: boolean;
  isVault?: boolean;
  // NOTE: would be better to actually define the size with numbers, to be able to calculate
  isLarge?: boolean;
  isLockedAsset?: boolean;
  style?: any;
  color?: string;
}

const CurrencyIcon = ({
  className = 'w-10 h-10',
  isVault = false,
  cornerIcon = false,
  currencyCode,
  code,
  constituent,
  inactive,
  isLarge,
  isLockedAsset,
  color,
  style,
}: CurrencyIconProps) => {
  const upperCaseCode = (currencyCode ?? code)?.toUpperCase();
  const lowerCaseCode = (currencyCode ?? code)?.toLowerCase();

  const { product } = useProduct(upperCaseCode);
  const productColor =
    color ??
    (product?.colors?.primaryStart && product?.colors?.primaryEnd
      ? {
          start: product?.colors?.primaryStart,
          end: product?.colors?.primaryEnd,
        }
      : product?.colors?.primary);

  let backgroundColor = productColor ?? colors.sky;
  let icon = `${cdnURL}assets/products/icons/${lowerCaseCode}.svg`;

  const fallbackIcon = `${cdnURL}assets/products/constituents/dark/fallback.svg`;
  const iconElement = useRef<any>(undefined);

  const displayCode =
    currencyCode !== null ? getDisplayAssetCode(lowerCaseCode || '') : '';

  const isBundle = product?.type?.toUpperCase() === 'BUNDLE';
  const isFiat = product?.type?.toUpperCase() === 'FIAT';
  const isTheme = product?.type?.toUpperCase() === 'THEME';
  // SET ICON & COLOR

  if (isBundle) {
    backgroundColor = productColor || theme.rvx;
  }

  if (isTheme) {
    backgroundColor = productColor || theme.violet;
  }

  if (constituent) {
    icon = `${cdnURL}assets/products/constituents/dark/${lowerCaseCode}.svg`;
    backgroundColor = 'transparent';
  }

  if (isVault) {
    backgroundColor = theme.rvxVault;
  }

  if (isLockedAsset) {
    backgroundColor = colors.darkGrey;
  }

  if (isFiat) {
    backgroundColor = 'transparent';
  }

  useEffect(() => {
    if (!constituent) return;
    const iconImg = new Image();
    iconImg.onerror = () => {
      iconImg.src = fallbackIcon;
    };

    iconImg.onload = () => {
      if (iconElement.current)
        iconElement.current.style.backgroundImage = `url(${iconImg.src})`;
    };

    iconImg.src = icon;
  }, [iconElement, currencyCode, constituent, icon, fallbackIcon]);

  return (
    <GradientOrColorView
      style={{
        backgroundSize: constituent ? 'contain' : '',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        ...style,
      }}
      colorOrGradient={backgroundColor}
      className={`relative h-10 w-10 rounded-full flex items-center justify-center ${className || ''} ${isLarge ? 'w-24 h-24' : ''}`}>
      {constituent ? (
        <img
          src={icon}
          alt={`${displayCode}-icon`}
          onError={imageLoadError}
          className={clsx('w-full h-auto', {
            'opacity-50': inactive,
          })}
        />
      ) : (
        <>
          <img
            src={icon}
            alt={`${displayCode}-icon`}
            onError={imageLoadError}
            className={clsx('w-full h-auto', {
              'opacity-50': inactive,
            })}
          />
          {cornerIcon && (
            <img
              src={cornerLockIcon}
              alt="lock-icon"
              className={`absolute -top-2 -left-2 ${
                isLarge ? 'w-12 h-12' : 'w-5 h-5'
              }`}
            />
          )}
        </>
      )}
    </GradientOrColorView>
  );
};

export default CurrencyIcon;
